
import React, { useEffect, useRef, memo, useCallback } from 'react';
import { Code, Copy } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface CodeEditorProps {
  htmlContent: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

export const CodeEditor = memo(function CodeEditor({ 
  htmlContent, 
  onChange 
}: CodeEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const changeTimeoutRef = useRef<number | null>(null);
  const isMountedRef = useRef<boolean>(true);
  
  // Focus the editor when it's first rendered
  useEffect(() => {
    isMountedRef.current = true;
    
    const timer = setTimeout(() => {
      if (textareaRef.current && isMountedRef.current) {
        textareaRef.current.focus();
      }
    }, 100);
    
    return () => {
      isMountedRef.current = false;
      clearTimeout(timer);
      if (changeTimeoutRef.current) {
        clearTimeout(changeTimeoutRef.current);
      }
    };
  }, []);
  
  // Handle tab key to insert spaces instead of changing focus - optimized with useCallback
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      
      const textarea = e.currentTarget;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      // Insert 2 spaces at cursor position
      const newValue = textarea.value.substring(0, start) + '  ' + textarea.value.substring(end);
      
      // Update the value and cursor position
      textarea.value = newValue;
      textarea.selectionStart = textarea.selectionEnd = start + 2;
      
      // Trigger the onChange event manually
      const event = Object.create(e);
      event.target = textarea;
      event.currentTarget = textarea;
      onChange(event as any);
    }
  }, [onChange]);
  
  // Optimized copy function with useCallback
  const copyToClipboard = useCallback(() => {
    if (navigator.clipboard && htmlContent) {
      navigator.clipboard.writeText(htmlContent)
        .then(() => toast.success("HTML copied to clipboard"))
        .catch(() => toast.error("Failed to copy HTML"));
    }
  }, [htmlContent]);
  
  // Optimized change handler with debouncing
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (changeTimeoutRef.current) {
      clearTimeout(changeTimeoutRef.current);
    }
    
    // Call onChange immediately for better UX
    onChange(e);
    
    // But set a timeout to prevent excessive additional processing
    changeTimeoutRef.current = window.setTimeout(() => {
      changeTimeoutRef.current = null;
    }, 200);
  }, [onChange]);
  
  return (
    <div className="editor-container h-full flex flex-col bg-white">
      <div className="h-[50px] flex-none p-3 bg-muted/50 border-b w-full flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Code className="h-4 w-4" />
          <h3 className="text-sm font-medium">HTML Editor</h3>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={copyToClipboard} 
            title="Copy HTML"
          >
            <Copy className="h-4 w-4" />
          </Button>
          <div className="text-xs text-muted-foreground">
            <span>Use <code>&#123;&#123;variableName&#125;&#125;</code> for dynamic content</span>
          </div>
        </div>
      </div>
      
      <div className="flex-1 relative w-full overflow-auto h-full code-editor-container">
        <Textarea 
          ref={textareaRef}
          name="html_content"
          className="w-full h-full font-mono text-sm rounded-none border-0 resize-none p-4 focus-visible:ring-0 focus-visible:ring-offset-0"
          value={htmlContent} 
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          spellCheck={false}
          style={{ 
            minHeight: '550px',
            height: '100%',
            lineHeight: '1.5',
            tabSize: 2,
            overflowY: 'auto',
          }}
          placeholder="Enter your HTML email content here..."
        />
      </div>
    </div>
  );
});

CodeEditor.displayName = 'CodeEditor';
